  // src/components/AnnexPanel.js
import React, { useState, useEffect } from 'react';
import Button from '../../components/buttons/Button';
import Card from '../../components/Card/Card';
import { FaCamera, FaFileImport, FaTimes, FaEye, FaEyeSlash, FaTrash, FaSpinner, FaDesktop, FaPaste } from 'react-icons/fa';
import ScreenshotPicker from './ScreenshotPicker'; 
import { isCapacitor, isElectron } from '../../utils/platform';
import { Clipboard } from '@capacitor/clipboard';
import FullScreenTextEditor from '../../components/editors/FullScreenTextEditor';
import { useFullScreenTextEditor } from '../../hooks/useFullScreenTextEditor';
import HoverTooltip from '../../components/tooltips/HoverTooltip';
import './AnnexPannel.css';
import { createLogger } from '../../services/Debug';


export default function AnnexPanel({
  annexText,
  setAnnexText,
  onPaste,
  onClearAnnex,
  onTakePhoto,
  onSelectFromLibrary,
  fileInputRef,
  onFileChange,
  onClose,
  isExtractingText,
  ocrQueueLength,
  onClearOcrQueue,
  onScreenshotCapture,
  processingProgress = { current: 0, total: 0 }
}) {
  const log = createLogger('AnnexPanel');
  const [showScreenshotPicker, setShowScreenshotPicker] = useState(false);
  const [sources, setSources] = useState([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isAnnexVisible, setIsAnnexVisible] = useState(false);
  // Entities are directly merged into annex text; no separate UI state

  // Setup fullscreen editor for all platforms
  const {
    openEditor: openFullScreenEditor,
    editorProps: fullScreenEditorProps,
    isEnabled: isFullScreenEnabled
  } = useFullScreenTextEditor({
    title: 'Modifier l\'annexe',
    onSave: async (newText) => {
      setAnnexText(newText);
    },
    showCharCount: true,
    placeholder: 'Collez ou écrivez ici l\'annexe à joindre à votre rapport.'
  });

  // Charger les sources d'écran disponibles
  useEffect(() => {
    loadSources();
  }, []);

  async function loadSources() {
    try {
      if (!window.electronAPI) return;
      const srcs = await window.electronAPI.listAvailableSources();
      
      // Trier les sources pour mettre l'écran entier en premier
      const sortedSources = [...srcs].sort((a, b) => {
        // Vérifier si le nom contient "Entire Screen" ou "Écran entier"
        const aIsEntireScreen = a.name.toLowerCase().includes('entire screen') || a.name.toLowerCase().includes('écran entier');
        const bIsEntireScreen = b.name.toLowerCase().includes('entire screen') || b.name.toLowerCase().includes('écran entier');
        
        if (aIsEntireScreen && !bIsEntireScreen) return -1;
        if (!aIsEntireScreen && bIsEntireScreen) return 1;
        return 0;
      });
      
      setSources(sortedSources);
    } catch (err) {
      log.error('Error listing sources =>', err);
    }
  }

  function handleScreenshotDone(screenshotDataUrl) {
    if (screenshotDataUrl) {
      onScreenshotCapture(screenshotDataUrl);
    }
    setShowScreenshotPicker(false);
  }

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      onFileChange({ target: { files } });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleOverlayClick = (e, closeFunction) => {
    // Si on clique sur l'overlay lui-même (pas ses enfants)
    if (e.target.className === 'dictaphone-overlay') {
      closeFunction();
    }
  };

  // Capacitor paste function
  const handleCapacitorPaste = async () => {
    try {
      const { type, value } = await Clipboard.read();
      if (value) {
        setAnnexText((prev) => {
          if (!prev.trim()) {
            return `Prochain annexe:\n${value}\n`;
          } else {
            return `${prev}\nProchain annexe:\n${value}\n`;
          }
        });
      }
    } catch (err) {
      log.error('Error pasting from clipboard:', err);
      // Fallback to browser clipboard API
      onPaste();
    }
  };

  // ETT entities support removed

  return (
    <>
      <Card 
        className="annex-panel-card"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      > 
        <div className="annex-panel-header">
          <h4 className="annex-panel-title">Annexe</h4>
          <Button
            type="inversed"
            icon={FaTimes}
            variant="secondary" 
            onClick={onClose}
            iconSize="small"
          />
        </div>

        {/* Actions sur deux lignes */}
        {!showScreenshotPicker && (
          <div className="annex-panel-actions-2lines">
            {/* Ligne 1 : Capture d'écran et Importer */}
            <div className="annex-panel-actions-row annex-panel-actions-row--two">
              {isCapacitor ? (
                <Button 
                  icon={FaCamera}
                  variant="reporttype-consultation"
                  onClick={onTakePhoto}
                  collapseOnSmallScreen={false}
                >
                  Prendre une photo
                </Button>
              ) : (
                <Button 
                  icon={FaDesktop}
                  variant="reporttype-consultation"
                  onClick={isElectron ? () => setShowScreenshotPicker(true) : onSelectFromLibrary}
                  collapseOnSmallScreen={false}
                >
                  Capture d'écran
                </Button>
              )}
              <Button 
                icon={FaFileImport} 
                variant="info" 
                onClick={onSelectFromLibrary}
              >
                Importer
              </Button>
            </div>
            {/* Ligne 2 : Voir annexe, Coller, Supprimer */}
            <div className="annex-panel-actions-row annex-panel-actions-row--three">
              <HoverTooltip text={isAnnexVisible ? "Masquer l'annexe" : "Voir l'annexe"}>
                <Button
                  type="inversed"
                  icon={isAnnexVisible ? FaEyeSlash : FaEye}
                  variant="secondary"
                  onClick={() => {
                    if (isFullScreenEnabled && !isAnnexVisible) {
                      // Open fullscreen editor when showing annex on all platforms
                      openFullScreenEditor(annexText);
                    } else {
                      // Toggle annex visibility
                      setIsAnnexVisible(!isAnnexVisible);
                    }
                  }}
                  iconSize="default"
                >
                </Button>
              </HoverTooltip>
              <HoverTooltip text={"Coller le presse-papiers dans l'annexe"}>
                <Button
                  type="inversed"
                  icon={FaPaste}
                  variant="secondary"
                  onClick={() => {
                    if (isCapacitor) {
                      handleCapacitorPaste();
                    } else {
                      onPaste();
                    }
                  }}
                  iconSize="default"
                >
                </Button>
              </HoverTooltip>
              <HoverTooltip text={"Supprimer l'annexe"}>
                <Button
                  type="inversed"
                  icon={FaTrash}
                  variant="danger" 
                  onClick={onClearAnnex}
                  iconSize="default"
                >
                </Button>
              </HoverTooltip>
            </div>
          </div>
        )}

        {/* Annex Text Area */}
        {isAnnexVisible && (
          <div className="annex-textarea-container">
            <textarea
              className="annex-textarea"
              value={annexText}
              onChange={(e) => setAnnexText(e.target.value)}
              placeholder="Collez ou écrivez ici l'annexe à joindre à votre rapport."
              onClick={(e) => {
                if (isFullScreenEnabled) {
                  e.preventDefault();
                  e.target.blur();
                  openFullScreenEditor(annexText);
                }
              }}
              onFocus={(e) => {
                if (isFullScreenEnabled) {
                  e.preventDefault();
                  e.target.blur();
                  openFullScreenEditor(annexText);
                }
              }}
              readOnly={isFullScreenEnabled}
            />
          </div>
        )}
        
        {/* Entities Panel removed: data goes directly into annex */}

        {/* Cancel OCR button - show only when OCR is processing */}
        {isExtractingText && ocrQueueLength > 0 && (
          <div className="annex-ocr-cancel">
            <Button 
              icon={FaTimes}
              variant="danger"
              onClick={() => {
                onClearOcrQueue();
                onClose();
              }}
              collapseOnSmallScreen={false}
            >
              Annuler
            </Button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          accept="image/*,.pdf,.doc,.docx,.txt"
          onChange={onFileChange}
          multiple
        />
      </Card>

      {showScreenshotPicker && (
        <ScreenshotPicker
          onClose={() => setShowScreenshotPicker(false)}
          onScreenshotDone={handleScreenshotDone}
        />
      )}

      {isExtracting && (
        <div className="dictaphone-overlay screenshot-picker-overlay">
          <Card className="dictaphone-overlay-card screenshot-picker-card">
            <div className="screenshot-picker-loading">
              <FaSpinner className={`dictaphone-spinner 'spinner-rouge'}`} />
            </div>
          </Card>
        </div>
      )}

      {/* FullScreen Editor */}
      <FullScreenTextEditor {...fullScreenEditorProps} />
    </>
  );
}